// src/components/family/profile/ChildProfileHeader.tsx
// Profile header with avatar, child info, and quick actions

import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Camera,
  Edit2,
  Download,
  Share2,
  MoreHorizontal,
  AlertCircle,
  Calendar,
  Cake,
  MapPin,
  Heart,
  Activity,
  ChevronDown,
  Check,
  X,
} from 'lucide-react';
import { formatDistanceToNow, differenceInYears, differenceInMonths, format } from 'date-fns';
import type { Child, ChildDiagnosis } from '@/types';
import { useUpdateChildAvatar, useGenerateEmergencyCard } from '@/features/children/hooks/useChildProfile';

interface ChildProfileHeaderProps {
  child: Child;
  primaryDiagnosis?: ChildDiagnosis;
  onEdit?: () => void;
  className?: string;
}

export const ChildProfileHeader: React.FC<ChildProfileHeaderProps> = ({
  child,
  primaryDiagnosis,
  onEdit,
  className = '',
}) => {
  const [showActions, setShowActions] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  const updateAvatar = useUpdateChildAvatar(child.id);
  const generateEmergencyCard = useGenerateEmergencyCard(child.id);

  // Calculate age
  const birthDate = new Date(child.dateOfBirth);
  const years = differenceInYears(new Date(), birthDate);
  const months = differenceInMonths(new Date(), birthDate) % 12;
  
  const ageDisplay = years > 0 
    ? `${years} year${years !== 1 ? 's' : ''}${months > 0 ? `, ${months} month${months !== 1 ? 's' : ''}` : ''}`
    : `${months} month${months !== 1 ? 's' : ''}`;

  // Get initials for avatar fallback
  const initials = `${child.firstName[0]}${child.lastName[0]}`.toUpperCase();

  // Avatar colors based on name (consistent for each child)
  const avatarColors = [
    'from-sage to-sage-dark',
    'from-terracotta to-terracotta-light',
    'from-navy to-navy-light',
    'from-purple-500 to-purple-600',
    'from-blue-500 to-blue-600',
    'from-pink-500 to-pink-600',
  ];
  const colorIndex = (child.firstName.charCodeAt(0) + child.lastName.charCodeAt(0)) % avatarColors.length;
  const avatarGradient = avatarColors[colorIndex];

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    setIsUploadingAvatar(true);
    try {
      await updateAvatar.mutateAsync(file);
    } catch (error) {
      console.error('Failed to upload avatar:', error);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleDownloadEmergencyCard = async () => {
    try {
      const { downloadUrl } = await generateEmergencyCard.mutateAsync();
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Failed to generate emergency card:', error);
    }
  };

  // Close actions menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setShowActions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`bg-white rounded-2xl shadow-soft border border-cream-dark overflow-hidden ${className}`}>
      {/* Banner gradient */}
      <div className={`h-24 sm:h-32 bg-gradient-to-r ${avatarGradient} relative`}>
        <div className="absolute inset-0 bg-black/5" />
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="px-4 sm:px-6 pb-6">
        {/* Avatar and info row */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 sm:-mt-16">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <button
              onClick={handleAvatarClick}
              disabled={isUploadingAvatar}
              className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-2xl overflow-hidden ring-4 ring-white shadow-lg group cursor-pointer disabled:cursor-not-allowed"
              aria-label="Change profile photo"
            >
              {child.avatarUrl ? (
                <img
                  src={child.avatarUrl}
                  alt={`${child.firstName}'s photo`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className={`w-full h-full bg-gradient-to-br ${avatarGradient} flex items-center justify-center`}>
                  <span className="text-3xl sm:text-4xl font-display font-bold text-white">
                    {initials}
                  </span>
                </div>
              )}

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-200">
                {isUploadingAvatar ? (
                  <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-8 h-8 text-white" />
                )}
              </div>
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Info section */}
          <div className="flex-1 sm:pb-2">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              {/* Name and details */}
              <div>
                <h1 className="text-2xl sm:text-3xl font-display font-semibold text-navy">
                  {child.nickname || child.firstName} {child.lastName}
                </h1>
                
                {child.nickname && (
                  <p className="text-sm text-warm-gray mt-0.5">
                    Legal name: {child.firstName} {child.lastName}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-navy-light">
                  <span className="flex items-center gap-1.5">
                    <Cake className="w-4 h-4 text-sage" />
                    {ageDisplay} old
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4 text-sage" />
                    {format(birthDate, 'MMMM d, yyyy')}
                  </span>
                  {child.birthCity && child.birthState && (
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-sage" />
                      {child.birthCity}, {child.birthState}
                    </span>
                  )}
                </div>

                {/* Primary diagnosis badge */}
                {primaryDiagnosis && (
                  <div className="mt-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sage/10 text-sage-dark rounded-full text-sm font-medium">
                      <Heart className="w-3.5 h-3.5" />
                      {primaryDiagnosis.diagnosis?.name || 'Primary Diagnosis'}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2" ref={actionsRef}>
                <button
                  onClick={onEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-xl font-medium text-sm hover:bg-sage-dark transition-colors duration-200 shadow-sm"
                >
                  <Edit2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Edit Profile</span>
                  <span className="sm:hidden">Edit</span>
                </button>

                {/* More actions dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowActions(!showActions)}
                    className="w-10 h-10 flex items-center justify-center rounded-xl bg-cream-dark text-navy-light hover:bg-cream hover:text-navy transition-colors duration-200"
                    aria-label="More actions"
                    aria-expanded={showActions}
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </button>

                  {showActions && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-medium border border-cream-dark py-1 z-20">
                      <button
                        onClick={handleDownloadEmergencyCard}
                        disabled={generateEmergencyCard.isPending}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy hover:bg-cream-dark transition-colors text-left disabled:opacity-50"
                      >
                        <AlertCircle className="w-4 h-4 text-terracotta" />
                        Download Emergency Card
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy hover:bg-cream-dark transition-colors text-left">
                        <Activity className="w-4 h-4 text-sage" />
                        View Health Summary
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy hover:bg-cream-dark transition-colors text-left">
                        <Share2 className="w-4 h-4 text-navy-light" />
                        Share with Provider
                      </button>
                      <hr className="my-1 border-cream-dark" />
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy hover:bg-cream-dark transition-colors text-left">
                        <Download className="w-4 h-4 text-navy-light" />
                        Export All Data
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Loading skeleton
export const ChildProfileHeaderSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl shadow-soft border border-cream-dark overflow-hidden animate-pulse">
    <div className="h-24 sm:h-32 bg-cream-dark" />
    
    <div className="px-4 sm:px-6 pb-6">
      <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 sm:-mt-16">
        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl bg-cream ring-4 ring-white" />
        
        <div className="flex-1 sm:pb-2 space-y-3">
          <div className="h-8 bg-cream-dark rounded-lg w-48" />
          <div className="flex gap-4">
            <div className="h-4 bg-cream-dark rounded w-24" />
            <div className="h-4 bg-cream-dark rounded w-32" />
          </div>
          <div className="h-7 bg-cream-dark rounded-full w-36" />
        </div>
      </div>
    </div>
  </div>
);

export default ChildProfileHeader;
